import React, { useState, useEffect, useRef } from 'react';
import './DataOverlays.css';
import { getTractsByOverlayId } from '../services/overlays';

const overlayOptions = [
  {
    id: 'censusTracts',
    name: 'Census Tracts',
    icon: '🗺️',
    description: 'View tract boundaries & income'
  },
  {
    id: 'demographics',
    name: 'Demographics',
    icon: '👥',
    description: 'Population density, age, income'
  },
  {
    id: 'eviction',
    name: 'Eviction Rates',
    icon: '🏠',
    description: 'Historical eviction data'
  },
  {
    id: 'income',
    name: 'Income Levels',
    icon: '💰',
    description: 'FFIEC median income by tract'
  }
];


const DataOverlays = ({ overlays, setOverlays, viewport, dataLayer, setDataLayer, onOverlayDataChange }) => {
  const [loading, setLoading] = useState({});
  const [overlayData, setOverlayData] = useState({
    demographics: null,
    eviction: null,
    income: null
  });
  const [lastViewport, setLastViewport] = useState(null);
  const [error, setError] = useState(null);

  const updateTimeoutRef = useRef(null);
  const lastFetchTimeRef = useRef({});

  const MIN_FETCH_INTERVAL = 3000;

  const fetchOverlayData = async (overlayId, currentViewport) => {
    const now = Date.now();
    const lastFetch = lastFetchTimeRef.current[overlayId] || 0;

    if (now - lastFetch < MIN_FETCH_INTERVAL) {
      console.log(`⏱️ Skipping ${overlayId} fetch - too soon`);
      return null;
    }

    try {
      setError(null);
      let params = {};
      const data = await getTractsByOverlayId(overlayId, params);
      lastFetchTimeRef.current[overlayId] = Date.now();
      return data;
    } catch (error) {
      console.error(`❌ Error fetching ${overlayId}:`, error);
      setError(`Failed to load ${overlayId} data: ${error.message}`);
      return null;
    }
  };

  const toggleOverlay = async (overlayId) => {
    const newState = !overlays[overlayId];

    setOverlays(prev => {
      const updated = {
        ...prev,
        [overlayId]: newState,
      };

      // Always keep censusTracts ON if any other overlay is turned on
      if (overlayId !== 'censusTracts' && newState === true) {
        updated['censusTracts'] = true;
      }

      return updated;
    });
    if (newState && overlayId === 'censusTracts') {
      setLoading(prev => ({ ...prev, [overlayId]: true }));

      const data = await fetchOverlayData(overlayId, viewport);

      if (data) {
        setOverlayData(prev => {
          const updated = {
            ...prev,
            [overlayId]: data
          };
          // Notify parent component of data changes
          if (onOverlayDataChange) {
            onOverlayDataChange(updated);
          }
          return updated;
        });
      }

      setLoading(prev => ({ ...prev, [overlayId]: false }));
    }
  };

  useEffect(() => {
    if (!viewport) return;

    const hasSignificantChange = !lastViewport ||
      Math.abs(viewport.latitude - lastViewport.latitude) > 0.15 ||
      Math.abs(viewport.longitude - lastViewport.longitude) > 0.15 ||
      Math.abs(viewport.zoom - lastViewport.zoom) > 3;

    if (hasSignificantChange && overlays.demographics && !loading.demographics) {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [viewport, overlays.demographics, loading.demographics]);

  return (
    <div className="sidebar-section">
      <h3 className="sidebar-title">Data Layers</h3>

      {error && (
        <div className="overlay-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 4h2v5H7V4zm0 6h2v2H7v-2z" />
          </svg>
          <div className="error-content">
            <div className="error-title">Error</div>
            <div className="error-message">{error}</div>
            <button
              className="error-dismiss"
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="overlay-list">
        {overlayOptions.map(overlay => (
          <div
            key={overlay.id}
            className={`overlay-item ${overlays[overlay.id] ? 'active' : ''}`}
          >
            <button
              className="overlay-toggle"
              onClick={() => toggleOverlay(overlay.id)}
              disabled={loading[overlay.id]}
            >
              <div className="overlay-info">
                <div className="overlay-name">
                  <span className="overlay-icon">{overlay.icon}</span>
                  {overlay.name}
                  {loading[overlay.id] && (
                    <span className="loading-indicator">
                      <span className="loading-dot"></span>
                      <span className="loading-dot"></span>
                      <span className="loading-dot"></span>
                    </span>
                  )}
                </div>
                <div className="overlay-description">
                  {overlay.description}
                </div>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={overlays[overlay.id]}
                  onChange={() => { }}
                  aria-label={`Toggle ${overlay.name}`}
                />
                <span className="toggle-slider"></span>
              </div>
            </button>


          </div>
        ))}
      </div>

      {/* Data Layer Visualization Selector */}
      {/* {(overlays.censusTracts || overlays.income || overlays.demographics) && (
        <div className="data-layer-selector">
          <label className="data-layer-label">Visualization:</label>
          <select
            className="data-layer-select"
            value={dataLayer || 'income'}
            onChange={(e) => setDataLayer && setDataLayer(e.target.value)}
          >
            <option value="income">Income Level</option>
            <option value="lmi">Low-to-Moderate Income %</option>
            <option value="population">Population</option>
            <option value="minority">Minority %</option>
            <option value="eviction">Eviction Rate</option>
          </select>
        </div>
      )} */}

      <div className="data-attribution">
        <p className="census-attribution">
          <strong>Attribution:</strong> This product uses the Census Bureau Data API but is not endorsed or certified by the Census Bureau.
        </p>
      </div>
    </div>
  );
};

export default DataOverlays;